import { ArrowLeft } from 'lucide-react';
import { useEffect } from 'react';

type LegalTopic =
  | 'privacy'
  | 'terms'
  | 'cookies'
  | 'acceptable-use'
  | 'disclaimer'
  | 'copyright'
  | 'gdpr'
  | 'data-retention'
  | 'dpa';

type Props = {
  topic: LegalTopic;
  onBack: () => void;
};

const CONTENT: Record<LegalTopic, { title: string; lastUpdated: string; sections: { heading: string; body: string[] }[] }> = {
  privacy: {
    title: 'Privacy Policy',
    lastUpdated: 'July 16, 2026',
    sections: [
      { heading: '1. Introduction', body: [
        'CareHire ("we", "us", "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and share your personal data when you use our platform.',
        'We are a data controller under the UK GDPR and Data Protection Act 2018. Our registered office is at CareHire Ltd, 123 Business Park, London, United Kingdom.',
      ]},
      { heading: '2. Data We Collect', body: [
        'Account data: your name, email address, agency name, and password (hashed).',
        'Candidate data: name, email, phone number, CV, and answers to screening questions submitted through application forms.',
        'Usage data: IP address, browser type, device information, and pages visited.',
        'Payment data: subscription status and billing references. We do not store card details.',
      ]},
      { heading: '3. How We Use Your Data', body: [
        'To provide and maintain the CareHire platform and its features.',
        'To send email notifications about new applications and account activity.',
        'To provide customer support and respond to enquiries.',
        'To monitor usage, detect fraud, and ensure platform security.',
        'To comply with legal obligations.',
      ]},
      { heading: '4. Legal Basis for Processing', body: [
        'Contract: processing necessary to provide the service you signed up for.',
        'Consent: for marketing communications and non-essential cookies.',
        'Legal obligation: for tax records and regulatory compliance.',
        'Legitimate interest: for platform security and fraud prevention.',
      ]},
      { heading: '5. Data Sharing', body: [
        'We do not sell your data. We share it only with:',
        'Payment processors — subscription and billing data.',
        'Supabase (database hosting) — all platform data.',
        'Email service providers — for notification delivery.',
        'Law enforcement when legally required to do so.',
      ]},
      { heading: '6. International Transfers', body: [
        'Your data may be processed outside the UK/EEA. We use providers that offer appropriate safeguards such as Standard Contractual Clauses.',
      ]},
      { heading: '7. Your Rights', body: [
        'Access: request a copy of your personal data.',
        'Rectification: correct inaccurate or incomplete data.',
        'Erasure: request deletion of your data ("right to be forgotten").',
        'Restriction: request we limit processing of your data.',
        'Portability: receive your data in a machine-readable format.',
        'Objection: object to processing based on legitimate interests.',
        'Withdraw consent: at any time, without affecting lawfulness of prior processing.',
      ]},
      { heading: '8. Data Retention', body: [
        'Account data is retained while your account is active and for 30 days after deletion.',
        'Candidate data is retained for the duration of the agency subscription plus 90 days.',
        'Usage logs are retained for 12 months.',
        'See our Data Retention Policy for full details.',
      ]},
      { heading: '9. Security', body: [
        'We use HTTPS/SSL encryption for all data in transit. Data at rest is encrypted by our hosting provider. Passwords are hashed using bcrypt. Access to personal data is restricted to authorised personnel only.',
      ]},
      { heading: '10. Children\'s Privacy', body: [
        'CareHire is not intended for use by anyone under 18. We do not knowingly collect data from children.',
      ]},
      { heading: '11. Changes to This Policy', body: [
        'We may update this policy from time to time. We will notify you of significant changes by email or in-app notification.',
      ]},
      { heading: '12. Contact', body: [
        'For privacy enquiries or to exercise your rights, email carehire@outlook.com. You can also lodge a complaint with the ICO at ico.org.uk.',
      ]},
    ],
  },
  terms: {
    title: 'Terms of Service',
    lastUpdated: 'July 16, 2026',
    sections: [
      { heading: '1. Agreement to Terms', body: [
        'By accessing or using CareHire, you agree to be bound by these Terms. If you disagree, you must not access the platform.',
      ]},
      { heading: '2. Definitions', body: [
        '"Platform" means the CareHire web application.',
        '"Agency" means a registered user who creates application forms and manages candidates.',
        '"Candidate" means an individual who submits an application through an agency\'s form.',
      ]},
      { heading: '3. Account Registration', body: [
        'You must provide accurate and complete information when registering.',
        'You are responsible for safeguarding your password and for all activity under your account.',
        'One agency account per user. Sharing accounts is prohibited.',
      ]},
      { heading: '4. Subscription and Billing', body: [
        'The platform is offered at $50/month.',
        'Subscriptions renew automatically until canceled. You can cancel anytime from your dashboard.',
        'Cancellation takes effect at the end of the current billing period. No refunds for partial months.',
        'We may change pricing with 30 days notice. Existing subscribers keep the current rate for the remainder of the billing period.',
      ]},
      { heading: '5. Acceptable Use', body: [
        'You must not use the platform to collect data deceptively, discriminate against candidates, or violate any law.',
        'You must not upload malware, attempt to breach security, or scrape data from other agencies.',
        'See our Acceptable Use Policy for full details.',
      ]},
      { heading: '6. Candidate Data', body: [
        'As an agency, you are the data controller for candidate data you collect. You are responsible for obtaining consent from candidates and complying with data protection law.',
        'CareHire acts as a data processor on your behalf.',
      ]},
      { heading: '7. Intellectual Property', body: [
        'The platform, its design, code, and branding are owned by CareHire Ltd.',
        'Content you upload (questions, logos) remains yours. You grant us a licence to host and display it for the purpose of providing the service.',
      ]},
      { heading: '8. Termination', body: [
        'You can cancel your subscription at any time.',
        'We may suspend or terminate accounts that violate these Terms or for non-payment.',
        'Upon termination, your data will be retained for 30 days then permanently deleted unless legally required otherwise.',
      ]},
      { heading: '9. Disclaimers', body: [
        'The platform is provided "as is" without warranties of any kind. We do not guarantee uninterrupted or error-free service.',
      ]},
      { heading: '10. Limitation of Liability', body: [
        'To the maximum extent permitted by law, CareHire shall not be liable for indirect, incidental, or consequential damages, or for loss of profits or data.',
      ]},
      { heading: '11. Governing Law', body: [
        'These Terms are governed by the laws of England and Wales. Disputes shall be resolved in the courts of London, United Kingdom.',
      ]},
      { heading: '12. Contact', body: [
        'For questions about these Terms, email carehire@outlook.com.',
      ]},
    ],
  },
  cookies: {
    title: 'Cookie Policy',
    lastUpdated: 'July 16, 2026',
    sections: [
      { heading: '1. What Are Cookies', body: [
        'Cookies are small text files stored on your device when you visit a website. They help us keep you logged in, remember preferences, and understand how the platform is used.',
      ]},
      { heading: '2. Types of Cookies We Use', body: [
        'Essential cookies: required for the platform to function (authentication, session management). These cannot be disabled.',
        'Preference cookies: remember your settings such as cookie consent choices.',
        'Analytics cookies: help us understand how visitors use the platform so we can improve it. We use anonymised, aggregated data only.',
      ]},
      { heading: '3. Third-Party Cookies', body: [
        'Supabase may set cookies for authentication. These are governed by their respective privacy policies.',
      ]},
      { heading: '4. Managing Cookies', body: [
        'You can manage or delete cookies through your browser settings. Essential cookies are required for the platform to work.',
        'You can also adjust your preferences using the cookie consent banner on our site.',
      ]},
      { heading: '5. Cookie Retention', body: [
        'Session cookies expire when you close your browser. Persistent cookies are retained for up to 12 months.',
      ]},
      { heading: '6. Changes', body: [
        'We may update this policy when we change the cookies we use. We will notify you via the consent banner.',
      ]},
    ],
  },
  'acceptable-use': {
    title: 'Acceptable Use Policy',
    lastUpdated: 'July 16, 2026',
    sections: [
      { heading: '1. Purpose', body: [
        'This Acceptable Use Policy ("AUP") sets out what you may and may not do when using CareHire.',
      ]},
      { heading: '2. Permitted Use', body: [
        'Use the platform to create application forms, collect candidate data lawfully, and manage recruitment.',
        'Upload accurate job listings and screening questions relevant to the role.',
        'Treat all candidates fairly and without discrimination.',
      ]},
      { heading: '3. Prohibited Conduct', body: [
        'Do not use the platform to collect data deceptively or without proper consent.',
        'Do not discriminate against candidates based on race, gender, religion, disability, age, sexual orientation, or any other protected characteristic.',
        'Do not upload or distribute malware, viruses, or harmful code.',
        'Do not attempt to access other agencies\' data or accounts.',
        'Do not scrape, crawl, or extract data from the platform.',
        'Do not use the platform for illegal activities or to facilitate any unlawful act.',
        'Do not send spam or unsolicited messages through the platform.',
        'Do not impersonate another person or organisation.',
      ]},
      { heading: '4. Content Standards', body: [
        'All content must be lawful, accurate, and not infringe the rights of any third party.',
        'Do not upload content that is defamatory, obscene, or otherwise objectionable.',
      ]},
      { heading: '5. Enforcement', body: [
        'Violations may result in content removal, account suspension, or termination.',
        'We may report illegal activity to the relevant authorities.',
      ]},
      { heading: '6. Reporting', body: [
        'To report misuse, email carehire@outlook.com with details and evidence.',
      ]},
    ],
  },
  disclaimer: {
    title: 'Disclaimer',
    lastUpdated: 'July 16, 2026',
    sections: [
      { heading: '1. General Disclaimer', body: [
        'CareHire is provided on an "as is" and "as available" basis. We make no warranties, express or implied, about the platform\'s reliability, availability, or fitness for a particular purpose.',
      ]},
      { heading: '2. No Guarantee of Results', body: [
        'We do not guarantee that using CareHire will result in successful hires or any specific outcome. Recruitment results depend on many factors outside our control.',
      ]},
      { heading: '3. Third-Party Services', body: [
        'We rely on third-party providers (Supabase, email services). We are not responsible for their downtime, data loss, or policy changes.',
      ]},
      { heading: '4. Professional Advice', body: [
        'Nothing on this platform constitutes legal, HR, or recruitment advice. You should consult qualified professionals for guidance specific to your situation.',
      ]},
      { heading: '5. User Content', body: [
        'We are not responsible for the accuracy of job listings, questions, or candidate data posted by agencies. Agencies are responsible for their own content.',
      ]},
      { heading: '6. Liability Limitation', body: [
        'To the fullest extent permitted by law, CareHire Ltd shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the platform.',
      ]},
    ],
  },
  copyright: {
    title: 'Copyright Notice',
    lastUpdated: 'July 16, 2026',
    sections: [
      { heading: '1. Ownership', body: [
        'All content on CareHire — including text, graphics, logos, code, design, and software — is owned by CareHire Ltd or its licensors and protected by UK and international copyright laws.',
      ]},
      { heading: '2. Trademarks', body: [
        '"CareHire" and the CareHire logo are trademarks of CareHire Ltd. You may not use them without prior written consent.',
      ]},
      { heading: '3. User Content', body: [
        'Content uploaded by agencies (logos, job listings, questions) remains the property of the respective agency. By uploading, you confirm you have the right to do so and grant CareHire a licence to host and display it.',
      ]},
      { heading: '4. Permitted Use', body: [
        'You may view, download, and print pages from the platform for your own personal, non-commercial use. You may not copy, modify, distribute, or republish any part of the platform without written permission.',
      ]},
      { heading: '5. Infringement', body: [
        'To report copyright infringement, email carehire@outlook.com with details of the work and its location on the platform.',
      ]},
    ],
  },
  gdpr: {
    title: 'GDPR Compliance',
    lastUpdated: 'July 16, 2026',
    sections: [
      { heading: '1. Our Commitment', body: [
        'CareHire is fully committed to compliance with the UK GDPR and Data Protection Act 2018. This page summarises how we meet our obligations.',
      ]},
      { heading: '2. Data Controller vs Processor', body: [
        'For agency account data, CareHire is the data controller.',
        'For candidate data collected through agency forms, the agency is the data controller and CareHire acts as a data processor.',
      ]},
      { heading: '3. Lawful Basis', body: [
        'We process personal data under the following lawful bases: contract (to provide the service), consent (for non-essential cookies and marketing), legal obligation (for tax and regulatory compliance), and legitimate interest (for security and fraud prevention).',
      ]},
      { heading: '4. Data Subject Rights', body: [
        'We facilitate the following rights: access, rectification, erasure, restriction, portability, objection, and withdrawal of consent.',
        'Agencies can export their data from the dashboard. Account deletion is available in Settings.',
        'Candidates can exercise their rights by contacting the agency directly or by emailing carehire@outlook.com.',
      ]},
      { heading: '5. Data Protection Officer', body: [
        'Our Data Protection Officer can be contacted at carehire@outlook.com.',
      ]},
      { heading: '6. Data Breach Procedure', body: [
        'In the event of a personal data breach, we will notify the ICO within 72 hours where required and inform affected individuals without undue delay.',
      ]},
      { heading: '7. International Transfers', body: [
        'We use Standard Contractual Clauses and other appropriate safeguards for international data transfers.',
      ]},
      { heading: '8. Data Processing Agreement', body: [
        'A Data Processing Agreement (DPA) is available for agencies who require one. Email carehire@outlook.com to request a copy.',
      ]},
      { heading: '9. Supervisory Authority', body: [
        'You have the right to lodge a complaint with the Information Commissioner\'s Office (ICO) at ico.org.uk.',
      ]},
    ],
  },
  'data-retention': {
    title: 'Data Retention Policy',
    lastUpdated: 'July 16, 2026',
    sections: [
      { heading: '1. Purpose', body: [
        'This policy defines how long different types of data are retained on the CareHire platform.',
      ]},
      { heading: '2. Retention Periods', body: [
        'Agency account data: retained while the subscription is active and for 30 days after account deletion.',
        'Candidate applications: retained for the duration of the agency subscription plus 90 days after cancellation.',
        'Screening questions and form data: retained with the agency account.',
        'CV files: retained with the application, then deleted from storage after the retention period.',
        'Usage and audit logs: retained for 12 months.',
        'Payment records: retained for 7 years for tax compliance.',
      ]},
      { heading: '3. Deletion Process', body: [
        'Data is permanently deleted after the retention period. CVs are removed from storage. Database records are purged.',
        'Agencies can request immediate deletion of all data from Settings > Account.',
      ]},
      { heading: '4. Legal Hold', body: [
        'Data subject to a legal hold or regulatory request will be retained until the hold is lifted, regardless of the standard retention period.',
      ]},
    ],
  },
  dpa: {
    title: 'Data Processing Agreement',
    lastUpdated: 'July 16, 2026',
    sections: [
      { heading: '1. Purpose', body: [
        'This Data Processing Agreement ("DPA") forms part of the Terms of Service and sets out the terms under which CareHire processes personal data on behalf of agencies.',
      ]},
      { heading: '2. Roles', body: [
        'The agency is the data controller. CareHire is the data processor. We process candidate data only on documented instructions from the agency.',
      ]},
      { heading: '3. Security Measures', body: [
        'Encryption in transit (TLS 1.2+) and at rest (AES-256).',
        'Row-level security and access controls.',
        'Regular security reviews and penetration testing.',
        'Audit logging for data access.',
      ]},
      { heading: '4. Sub-Processors', body: [
        'We use Supabase (database hosting) and email service providers. We ensure all sub-processors provide sufficient guarantees of data protection.',
      ]},
      { heading: '5. Data Subject Requests', body: [
        'We will assist agencies in responding to data subject requests where technically and legally possible.',
      ]},
      { heading: '6. Breach Notification', body: [
        'We will notify the agency of any personal data breach without undue delay and provide information needed to fulfil regulatory obligations.',
      ]},
      { heading: '7. Deletion on Termination', body: [
        'Upon account deletion, all agency and candidate data will be deleted within 30 days, unless legal obligations require longer retention.',
      ]},
      { heading: '8. Audit', body: [
        'Agencies may request information about our security practices. On-site audits are available for enterprise customers by arrangement.',
      ]},
      { heading: '9. Requesting This DPA', body: [
        'To receive a signed copy of this DPA, email carehire@outlook.com with your agency name and account email.',
      ]},
    ],
  },
};

export default function LegalPage({ topic, onBack }: Props) {
  const content = CONTENT[topic];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [topic]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={onBack} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-900 text-sm font-medium transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <span className="font-bold text-gray-900 text-sm">CareHire</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-10 pb-20">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{content.title}</h1>
        <p className="text-sm text-gray-400 mb-8">Last updated: {content.lastUpdated}</p>

        <div className="space-y-8">
          {content.sections.map((section, i) => (
            <section key={i}>
              <h2 className="text-lg font-bold text-gray-900 mb-3">{section.heading}</h2>
              {section.body.map((paragraph, j) => (
                <p key={j} className="text-sm text-gray-600 leading-relaxed mb-2">{paragraph}</p>
              ))}
            </section>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-gray-200">
          <p className="text-xs text-gray-400">
            Questions about this document? Email us at{' '}
            <a href="mailto:carehire@outlook.com" className="text-blue-600 hover:underline">carehire@outlook.com</a>
          </p>
        </div>
      </div>
    </div>
  );
}
